import {
  Component,
  AfterViewInit,
  Inject,
  NgZone,
  PLATFORM_ID,
  OnDestroy,
  Input,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

@Component({
  selector: 'app-stacked-cluster-chart',
  templateUrl: './stacked-cluster-chart.component.html',
  styleUrls: ['./stacked-cluster-chart.component.scss']
})
export class StackedClusterChartComponent implements AfterViewInit, OnDestroy, OnChanges {
  
  private root: am5.Root | undefined;
  @Input() data: any = [];
  @Input() fillData: boolean = false;
  

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private zone: NgZone
  ) {}

  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.root) {
      this.root.dispose();
    }
   this.createChart()
  }
  
  async createDataWithZero(data: any)  {
    const allKeys = Array.from(
      new Set(
        data.flatMap((obj: any) =>
          Object.keys(obj).filter(key => key !== 'Year')
        )
      )
    );
    const normalizedData = data?.map((obj: any) => {
      const newObj: any = { ...obj };

      allKeys.forEach((key: any) => {
        if (!(key in newObj)) {
          newObj[key] = 0;
        }
      });

      return newObj;
    });
    return normalizedData;
  }

  createChart(){
    this.browserOnly(async () => {
      let root = am5.Root.new('chartdiv');

      root.setThemes([am5themes_Animated.new(root)]);

      var chart = root.container.children.push(am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        paddingLeft: 20,
        layout: root.verticalLayout
      }));

      
      

      chart.set("colors", am5.ColorSet.new(root, {
        colors: [
          am5.color("#2372F0"),  
          am5.color("#568AEE"),  
          am5.color("#87ADF4"),  
          am5.color("#AFC7F6"),
          am5.color("#CDDEFF")   
        ],
        reuse: true  // Reuse colors if more slices exist
      }));
      
      
      // Add legend
      // https://www.amcharts.com/docs/v5/charts/xy-chart/legend-xy-series/
      var legend = chart.children.push(am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.percent(55),
        layout: root.horizontalLayout,
        paddingTop: 10,
        
      }));

      legend.markers.template.setAll({
        width: 12,
        height: 12
      });

      legend.itemContainers.template.set('marginLeft', -25)
      legend.itemContainers.template.set('marginRight', -25)
      



      legend.markerRectangles.template.setAll({
        cornerRadiusTL: 10,
        cornerRadiusTR: 10,
        cornerRadiusBL: 10,
        cornerRadiusBR: 10
      });

      legend.labels.template.setAll({
        fontSize: 12,
        fontWeight: "700",
        marginLeft: 5,
        marginRight:0
      });
     if(this.fillData){
        this.data = await this.createDataWithZero(this.data);
      }
      let datavalue = this.data.map((data: any)=>{
        return Object.fromEntries(
          Object.entries(data).filter(([key, value]) => value !== 0)
        );
      })
    
      
      var data = datavalue
      
      
      // Create axes
      // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
      var xRenderer = am5xy.AxisRendererX.new(root, {
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        minorGridEnabled: true,
        minGridDistance: 0 // all years are visible
      });
      
      var xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
        categoryField: "Year",
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {})
      }));
      
     
      xRenderer.grid.template.setAll({
        location: 1,
        forceHidden: true,
      });
      
      xAxis.data.setAll(data);
      
      var yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: am5xy.AxisRendererY.new(root, {
          strokeOpacity: 0.1
        })
      }));

      let yRenderer = yAxis.get('renderer');

      yRenderer.grid.template.set('forceHidden', true);
      
      
      // Add series
      // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
      function makeSeries(name: any, fieldName: any, stacked: any, radius = false ) {
        var series = chart.series.push(am5xy.ColumnSeries.new(root, {
          stacked: stacked,
          name: name,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: fieldName,
          categoryXField: "Year"
        }));
      
        series.columns.template.setAll({
          tooltipText: "{name} : {valueY}",
          width: am5.percent(90),
          tooltipY: am5.percent(10),
          maxWidth: 64,
        });

        // if(radius){
        //   series.columns.template.set('cornerRadiusTL', 10)
        //   series.columns.template.set('cornerRadiusTR', 10)
        // }

        series.columns.template.adapters.add("cornerRadiusTL", function(radius, target) {
          return isTop(target.dataItem) ? 10 : 0;
        });
        series.columns.template.adapters.add("cornerRadiusTR", function(radius, target) {
          return isTop(target.dataItem) ? 10 : 0;
        });

        function getCategoryDataItems(dataItem: any) {
          var targetSeries = dataItem.component;
          var currentStack = false;
          var index = xAxis.categoryToIndex(dataItem.get("categoryX"));
          var items: any = [];
          chart.series.each(function(series) {
            if (!series.get("stacked")) {
              if (currentStack) {
                return items;
              }
              else {
                items = [];
              }
            }
            if (series == targetSeries) {
              currentStack = true;
            }
            if (series.get("visible")) {
              items.push(series.dataItems[index]);
            }
          });
          return items;
        }

        function isTop(dataItem: any) {
          var items: any = getCategoryDataItems(dataItem);
          var targetTop = 0;
          var maxTop = 100000;
          am5.array.each(items, function(item: any) {
            var top = item.get("top");
            if (item === dataItem) {
              targetTop = top;
            }
            if (maxTop > top) {
              maxTop = top;
            }
          });
          return targetTop == maxTop;
        }
        // series.columns.template.adapters.add("cornerRadiusTL", function(radius: any, target: any) {
        //   let value = target.dataItem.values.valueY.value;
        //   console.log(target.dataItem.index)
        //   return 10;
        // });
        series.data.setAll(data);

        
      
        // Make stuff animate on load
        // https://www.amcharts.com/docs/v5/concepts/animations/
        series.appear();
      
        // series.bullets.push(function () {
        //   return am5.Bullet.new(root, {
        //     locationY: 0.5,
        //     sprite: am5.Label.new(root, {
        //       text: "{valueY}",
        //       fill: root.interfaceColors.get("alternativeText"),
        //       centerY: am5.percent(50),
        //       centerX: am5.percent(50),
        //       populateText: true,
        //       fontSize: 12,
        //     })
        //   });
        // });
      
        legend.data.push(series);
      }

      
      const value = Object.keys(this.data[0])
      value.forEach(((data, index) => {
        if(data !== 'Year'){
          const radius = value.length - 1 === index;
          makeSeries(data, data, true, radius);
        }
      }))
      
      
      chart.appear(1000, 100);

      this.root = root;
    });
  }

  ngAfterViewInit() {
    // Chart code goes in here
    
  }

  ngOnDestroy() {
    // Clean up chart when the component is removed
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
    });
  }

}
